import { SlideObject, SlideObjectStyle } from '../types';
import {
  TABLE_FONT_SIZE,
  TABLE_MIN_FONT_SIZE,
  TABLE_MIN_STROKE_WIDTH,
  tableChrome,
} from './tableModel';

export type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export function snapValue(value: number, snap: boolean, grid = 8): number {
  return snap ? Math.round(value / grid) * grid : value;
}

export function clampCrop(value: number): number {
  return Math.max(0, Math.min(0.92, value));
}

export function applyHandleResize(
  object: Pick<SlideObject, 'x' | 'y' | 'w' | 'h' | 'style' | 'type'>,
  handle: ResizeHandle,
  dx: number,
  dy: number,
  options: { uniform?: boolean; snap?: boolean; minSize?: number } = {}
): Pick<SlideObject, 'x' | 'y' | 'w' | 'h' | 'style'> {
  const minSize = options.minSize ?? (object.style?.shape === 'line' ? 8 : 40);
  let x = object.x;
  let y = object.y;
  let w = object.w;
  let h = object.h;

  if (handle.includes('e')) w = Math.max(minSize, object.w + dx);
  if (handle.includes('w')) {
    w = Math.max(minSize, object.w - dx);
    x = object.x + object.w - w;
  }
  if (handle.includes('s')) h = Math.max(minSize, object.h + dy);
  if (handle.includes('n')) {
    h = Math.max(minSize, object.h - dy);
    y = object.y + object.h - h;
  }

  if (options.uniform && handle.length === 2) {
    const factor = Math.abs(dx) > Math.abs(dy) ? w / object.w : h / object.h;
    w = Math.max(minSize, object.w * factor);
    h = Math.max(minSize, object.h * factor);
    if (handle.includes('w')) x = object.x + object.w - w;
    if (handle.includes('n')) y = object.y + object.h - h;
    if (object.type === 'text' && object.style?.fontSize) {
      return {
        x: snapValue(x, Boolean(options.snap)),
        y: snapValue(y, Boolean(options.snap)),
        w: snapValue(w, Boolean(options.snap)),
        h: snapValue(h, Boolean(options.snap)),
        style: {
          ...object.style,
          fontSize: Math.max(TABLE_MIN_FONT_SIZE, Math.round(object.style.fontSize * factor)),
        },
      };
    }
    if (object.type === 'table') {
      const chrome = tableChrome(object.style);
      const fontSize = Number(object.style?.fontSize) || TABLE_FONT_SIZE;
      return {
        x: snapValue(x, Boolean(options.snap)),
        y: snapValue(y, Boolean(options.snap)),
        w: snapValue(w, Boolean(options.snap)),
        h: snapValue(h, Boolean(options.snap)),
        style: {
          ...object.style,
          fontSize: Math.max(TABLE_MIN_FONT_SIZE, Math.round(fontSize * factor)),
          padding: Math.max(1, chrome.padding * factor),
          strokeWidth: Math.max(TABLE_MIN_STROKE_WIDTH, chrome.strokeWidth * factor),
        },
      };
    }
  }

  return {
    x: snapValue(x, Boolean(options.snap)),
    y: snapValue(y, Boolean(options.snap)),
    w: snapValue(w, Boolean(options.snap)),
    h: snapValue(h, Boolean(options.snap)),
    style: object.style,
  };
}

export function applySideCrop(
  object: Pick<SlideObject, 'x' | 'y' | 'w' | 'h' | 'style'>,
  handle: ResizeHandle,
  dx: number,
  dy: number,
  snap = false
): Pick<SlideObject, 'x' | 'y' | 'w' | 'h' | 'style'> {
  const style = { ...(object.style || {}) };
  const left = style.cropLeft || 0;
  const right = style.cropRight || 0;
  const top = style.cropTop || 0;
  const bottom = style.cropBottom || 0;
  const visW = Math.max(0.08, 1 - left - right);
  const visH = Math.max(0.08, 1 - top - bottom);
  const imageW = object.w / visW;
  const imageH = object.h / visH;
  let x = object.x;
  let y = object.y;
  let w = object.w;
  let h = object.h;

  if (handle === 'e') {
    w = Math.min(imageW * (1 - left), Math.max(40, object.w + dx));
    style.cropRight = clampCrop(1 - left - w / imageW);
  } else if (handle === 'w') {
    w = Math.min(imageW * (1 - right), Math.max(40, object.w - dx));
    style.cropLeft = clampCrop(1 - right - w / imageW);
    x = object.x + object.w - w;
  } else if (handle === 's') {
    h = Math.min(imageH * (1 - top), Math.max(40, object.h + dy));
    style.cropBottom = clampCrop(1 - top - h / imageH);
  } else if (handle === 'n') {
    h = Math.min(imageH * (1 - bottom), Math.max(40, object.h - dy));
    style.cropTop = clampCrop(1 - bottom - h / imageH);
    y = object.y + object.h - h;
  }

  return {
    x: snapValue(x, snap),
    y: snapValue(y, snap),
    w: snapValue(w, snap),
    h: snapValue(h, snap),
    style,
  };
}

export function applyCropPan(
  style: SlideObjectStyle | undefined,
  dxFraction: number,
  dyFraction: number
): SlideObjectStyle {
  const left = style?.cropLeft || 0;
  const top = style?.cropTop || 0;
  const right = style?.cropRight || 0;
  const bottom = style?.cropBottom || 0;
  const visW = Math.max(0.08, 1 - left - right);
  const visH = Math.max(0.08, 1 - top - bottom);
  const nextLeft = Math.max(0, Math.min(1 - visW, left + dxFraction));
  const nextTop = Math.max(0, Math.min(1 - visH, top + dyFraction));
  return {
    ...style,
    cropLeft: nextLeft,
    cropTop: nextTop,
    cropRight: 1 - visW - nextLeft,
    cropBottom: 1 - visH - nextTop,
  };
}

export function rotationFromPointer(
  centerX: number,
  centerY: number,
  clientX: number,
  clientY: number
): number {
  return (Math.atan2(clientY - centerY, clientX - centerX) * 180) / Math.PI + 90;
}

export function normalizeRotation(degrees: number): number {
  let next = degrees % 360;
  if (next < 0) next += 360;
  return next;
}

export function rotatedAabb(x: number, y: number, w: number, h: number, rotation = 0): { x: number; y: number; w: number; h: number } {
  if (!rotation) return { x, y, w, h };
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const cx = x + w / 2;
  const cy = y + h / 2;
  const corners = [
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ].map(([px, py]) => {
    const dx = px - cx;
    const dy = py - cy;
    return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
  });
  const xs = corners.map((point) => point[0]);
  const ys = corners.map((point) => point[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
}

const SIDE_CYCLE: ResizeHandle[] = ['e', 's', 'w', 'n'];
const CORNER_CYCLE: ResizeHandle[] = ['ne', 'se', 'sw', 'nw'];

export function aabbQuarterTurns(rotation = 0): number {
  return Math.round(normalizeRotation(rotation) / 90) % 4;
}

export function mapVisualHandleToLocal(handle: ResizeHandle, rotation = 0): ResizeHandle {
  const turns = aabbQuarterTurns(rotation);
  // 0° and 180° keep the same AABB edges as the layout box.
  if (!turns || turns === 2) return handle;
  const cycle = handle.length === 1 ? SIDE_CYCLE : CORNER_CYCLE;
  const index = cycle.indexOf(handle);
  if (index < 0) return handle;
  return cycle[(index - turns + 4) % 4];
}

export function screenDeltaToLocal(dx: number, dy: number, rotation = 0): { dx: number; dy: number } {
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    dx: dx * cos + dy * sin,
    dy: -dx * sin + dy * cos,
  };
}

export function aabbDeltaToLocal(dx: number, dy: number, rotation = 0): { dx: number; dy: number } {
  const turns = aabbQuarterTurns(rotation);
  if (!turns || turns === 2) return { dx, dy };
  return screenDeltaToLocal(dx, dy, rotation);
}

export function rotateLocalPoint(
  originX: number,
  originY: number,
  w: number,
  h: number,
  localX: number,
  localY: number,
  rotation = 0,
): { x: number; y: number } {
  const px = originX + localX;
  const py = originY + localY;
  if (!rotation) return { x: px, y: py };
  const rad = (rotation * Math.PI) / 180;
  const cx = originX + w / 2;
  const cy = originY + h / 2;
  const dx = px - cx;
  const dy = py - cy;
  return {
    x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
    y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
  };
}

export function applyRotation(startRotation: number, startAngle: number, currentAngle: number, snap = true): number {
  let next = startRotation + (currentAngle - startAngle);
  if (snap) next = Math.round(next / 15) * 15;
  next %= 360;
  if (next > 180) next -= 360;
  if (next < -180) next += 360;
  return next;
}

export function uncroppedImageRect(
  object: Pick<SlideObject, 'w' | 'h' | 'style'>
): { x: number; y: number; w: number; h: number } {
  const left = object.style?.cropLeft || 0;
  const right = object.style?.cropRight || 0;
  const top = object.style?.cropTop || 0;
  const bottom = object.style?.cropBottom || 0;
  const visW = Math.max(0.08, 1 - left - right);
  const visH = Math.max(0.08, 1 - top - bottom);
  const imageW = object.w / visW;
  const imageH = object.h / visH;
  return {
    x: -left * imageW,
    y: -top * imageH,
    w: imageW,
    h: imageH,
  };
}

function cropsFromImageRect(
  frameW: number,
  frameH: number,
  image: { x: number; y: number; w: number; h: number }
): Pick<SlideObjectStyle, 'cropLeft' | 'cropTop' | 'cropRight' | 'cropBottom'> {
  let { x, y, w, h } = image;
  w = Math.max(frameW, w);
  h = Math.max(frameH, h);
  if (x > 0) x = 0;
  if (y > 0) y = 0;
  if (x + w < frameW) x = frameW - w;
  if (y + h < frameH) y = frameH - h;
  const cropLeft = clampCrop(-x / w);
  const cropTop = clampCrop(-y / h);
  return {
    cropLeft,
    cropTop,
    cropRight: clampCrop(1 - frameW / w - cropLeft),
    cropBottom: clampCrop(1 - frameH / h - cropTop),
  };
}

export function applyImageScaleFromHandle(
  object: Pick<SlideObject, 'x' | 'y' | 'w' | 'h' | 'style'>,
  handle: ResizeHandle,
  dx: number,
  dy: number
): Pick<SlideObject, 'x' | 'y' | 'w' | 'h' | 'style'> {
  const img = uncroppedImageRect(object);
  const resized = applyHandleResize(
    { x: img.x, y: img.y, w: img.w, h: img.h, type: 'image', style: {} },
    handle,
    dx,
    dy,
    { uniform: true, snap: false, minSize: 8 }
  );
  let next = { x: resized.x, y: resized.y, w: resized.w, h: resized.h };
  const cover = Math.max(object.w / next.w, object.h / next.h, 1);
  if (cover > 1) {
    const width = next.w * cover;
    const height = next.h * cover;
    if (handle.includes('w')) next.x = next.x + next.w - width;
    if (handle.includes('n')) next.y = next.y + next.h - height;
    next = { ...next, w: width, h: height };
  }
  return {
    x: object.x,
    y: object.y,
    w: object.w,
    h: object.h,
    style: { ...object.style, ...cropsFromImageRect(object.w, object.h, next) },
  };
}

export function coverCropInsets(
  frameW: number,
  frameH: number,
  imageW: number,
  imageH: number
): Pick<SlideObjectStyle, 'cropLeft' | 'cropTop' | 'cropRight' | 'cropBottom'> {
  if (frameW <= 0 || frameH <= 0 || imageW <= 0 || imageH <= 0) {
    return { cropLeft: 0, cropTop: 0, cropRight: 0, cropBottom: 0 };
  }
  const frameRatio = frameW / frameH;
  const imageRatio = imageW / imageH;
  if (imageRatio > frameRatio) {
    const inset = (1 - frameRatio / imageRatio) / 2;
    return { cropLeft: inset, cropTop: 0, cropRight: inset, cropBottom: 0 };
  }
  if (imageRatio < frameRatio) {
    const inset = (1 - imageRatio / frameRatio) / 2;
    return { cropLeft: 0, cropTop: inset, cropRight: 0, cropBottom: inset };
  }
  return { cropLeft: 0, cropTop: 0, cropRight: 0, cropBottom: 0 };
}

export function imageCropStyle(style?: SlideObjectStyle): {
  width: string;
  height: string;
  marginLeft?: string;
  marginTop?: string;
  objectFit: 'cover' | 'contain' | 'fill';
  maxWidth?: string;
} {
  const left = style?.cropLeft || 0;
  const top = style?.cropTop || 0;
  const right = style?.cropRight || 0;
  const bottom = style?.cropBottom || 0;
  const width = 1 - left - right;
  const height = 1 - top - bottom;
  if (width <= 0 || height <= 0 || (left === 0 && top === 0 && right === 0 && bottom === 0)) {
    return { width: '100%', height: '100%', objectFit: style?.objectFit || 'contain' };
  }
  return {
    width: `${100 / width}%`,
    height: `${100 / height}%`,
    marginLeft: `${(-left * 100) / width}%`,
    marginTop: `${(-top * 100) / height}%`,
    objectFit: 'contain',
    maxWidth: 'none',
  };
}
